/**
 * Per-family revenue charts on Revenue Stats (WSRADM-0052).
 *
 * One small BarChart card per visible family (respects hidden columns) using
 * rows already loaded on the page (no extra request). Scope = table months.
 */
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BarChart } from '@mui/x-charts/BarChart';
import { formatMonthLabel } from '@/api/reports/format';
import type { FamilyColumn, RevenueMonthRow } from '@/api/reports/types';
import { familyColorOrNeutral } from '@/components/familyPalette';
import { familyViewKey } from '@/config/viewPrefs';
import { useCopy } from '@/hooks/useCopy';
import { revenueYAxisValueFormatter } from '@/routes/dashboard/RevenueChart';

export interface RevenueByFamilyChartsProps {
  rows: readonly RevenueMonthRow[];
  familyColumns: readonly FamilyColumn[];
  hidden: ReadonlySet<string>;
  currency: string;
  dimensionName: string;
}

export function RevenueByFamilyCharts({
  rows,
  familyColumns,
  hidden,
  currency,
  dimensionName,
}: RevenueByFamilyChartsProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const theme = useTheme();

  if (rows.length === 0) return null;

  // Months oldest to newest for display
  const reversedRows = [...rows].reverse();
  const months = reversedRows.map((r) => r.month);

  const captionParts = [currency];
  if (dimensionName) captionParts.push(dimensionName);
  const caption = captionParts.join(' · ');

  const showMonthTotal = !hidden.has('monthTotal');
  const visibleFamilies = familyColumns.filter((column) => !hidden.has(familyViewKey(column.slug)));

  if (!showMonthTotal && visibleFamilies.length === 0) return null;

  return (
    <Box component="section" sx={{ mt: 5, mb: 4 }} data-testid="revenue-by-family-charts">
      <Typography variant="h2" sx={{ mb: 0.5 }}>
        {t('reports.revenueByFamily.title', 'Revenue by family, month by month')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {t('reports.revenueByFamily.scope', 'Months shown in the table above.')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(auto-fill, minmax(320px, 1fr))',
          },
          gap: 2,
        }}
      >
        {showMonthTotal ? (
          <Card variant="outlined" key="month-total" data-testid="revenue-family-chart-monthTotal">
            <CardContent>
              <Typography variant="subtitle1" component="h3">
                {t('reports.revenueByFamily.monthTotal', 'Month total')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {caption}
              </Typography>
              <Box sx={{ height: 180 }}>
                <BarChart
                  height={180}
                  xAxis={[
                    {
                      scaleType: 'band',
                      data: months,
                      valueFormatter: (m: string | null) =>
                        m ? formatMonthLabel(m, i18n.language) : '',
                    },
                  ]}
                  yAxis={[
                    {
                      valueFormatter: (v: number | null) =>
                        revenueYAxisValueFormatter(v, currency, i18n.language),
                    },
                  ]}
                  series={[
                    {
                      id: 'series-month-total',
                      data: reversedRows.map((r) => r.totalRevenue),
                      label: t('reports.revenueByFamily.monthTotal', 'Month total'),
                      color: theme.palette.text.primary,
                    },
                  ]}
                  margin={{ top: 10, bottom: 25, left: 45, right: 10 }}
                  hideLegend
                />
              </Box>
            </CardContent>
          </Card>
        ) : null}

        {visibleFamilies.map((column) => {
          const palette = familyColorOrNeutral(column.color);
          const color = palette?.bg ?? theme.palette.primary.main;
          const familyData = reversedRows.map(
            (r) => r.families.find((f) => f.familySlug === column.slug)?.revenue ?? 0,
          );

          return (
            <Card
              key={column.slug ?? 'unmapped'}
              variant="outlined"
              data-testid={`revenue-family-chart-${column.slug ?? 'unmapped'}`}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {palette ? (
                    <Box
                      component="span"
                      aria-hidden
                      data-testid={`revenue-chart-swatch-${column.slug ?? 'unmapped'}`}
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '2px',
                        mr: 1,
                        bgcolor: palette.bg,
                      }}
                    />
                  ) : null}
                  <Typography variant="subtitle1" component="h3">
                    {column.displayName}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {caption}
                </Typography>
                <Box sx={{ height: 180 }}>
                  <BarChart
                    height={180}
                    xAxis={[
                      {
                        scaleType: 'band',
                        data: months,
                        valueFormatter: (m: string | null) =>
                          m ? formatMonthLabel(m, i18n.language) : '',
                      },
                    ]}
                    yAxis={[
                      {
                        valueFormatter: (v: number | null) =>
                          revenueYAxisValueFormatter(v, currency, i18n.language),
                      },
                    ]}
                    series={[
                      {
                        id: `series-${column.slug ?? 'unmapped'}`,
                        data: familyData,
                        label: column.displayName,
                        color,
                      },
                    ]}
                    margin={{ top: 10, bottom: 25, left: 45, right: 10 }}
                    hideLegend
                  />
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}
